import api from './api';
import { BlogPost } from '../types/blog';

/**
 * Blog Service
 * Handles all blog-related API requests
 */

// API Response types
interface BlogPostResponse {
    success: boolean;
    data: any;
    message?: string;
}

interface BlogPostsListResponse {
    success: boolean;
    data: any[];
    total: number;
    page: number;
    limit: number;
}

// Request payload for creating/updating blog posts
interface BlogPostRequest {
    title: string;
    slug: string;
    content: string;
    excerpt: string;
    featured_image?: string;
    author: string;
    publish_date: string;
    status: 'draft' | 'published' | 'archived';
    comments_enabled: boolean;
    category_ids?: number[];
    tag_ids?: number[];
    // SEO fields
    meta_title?: string;
    meta_description?: string;
    meta_keywords?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    canonical_url?: string;
}

const blogService = {
    /**
     * Get all blog posts (admin)
     * @param status - Filter by status (all, draft, published, archived)
     * @param page - Page number
     * @param limit - Items per page
     * @returns Promise<BlogPostsListResponse>
     */
    getAll: async (status: string = 'all', page: number = 1, limit: number = 50): Promise<BlogPostsListResponse> => {
        try {
            const response = await api.get<BlogPostsListResponse>(
                `/api/admin/blog/posts?status=${status}&page=${page}&limit=${limit}`
            );
            return response;
        } catch (error) {
            console.error('Error fetching blog posts:', error);
            throw error;
        }
    },

    /**
     * Get published blog posts (public)
     * @param page - Page number
     * @param limit - Items per page
     * @returns Promise<BlogPostsListResponse>
     */
    getPublished: async (page: number = 1, limit: number = 10): Promise<BlogPostsListResponse> => {
        try {
            const response = await api.get<BlogPostsListResponse>(
                `/api/blog/posts/published?page=${page}&limit=${limit}`
            );
            return response;
        } catch (error) {
            console.error('Error fetching published blog posts:', error);
            throw error;
        }
    },

    /**
     * Get blog post by public_id
     * @param publicId - Blog post public_id (UUID)
     * @returns Promise<BlogPostResponse>
     */
    getByPublicId: async (publicId: string): Promise<BlogPostResponse> => {
        try {
            const response = await api.get<BlogPostResponse>(`/api/admin/blog/posts/public/${publicId}`);
            return response;
        } catch (error) {
            console.error(`Error fetching blog post ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get blog post by slug (public)
     * @param slug - Blog post slug
     * @returns Promise<BlogPostResponse>
     */
    getBySlug: async (slug: string): Promise<BlogPostResponse> => {
        try {
            const response = await api.get<BlogPostResponse>(`/api/blog/posts/slug/${slug}`);
            return response;
        } catch (error) {
            console.error(`Error fetching blog post with slug ${slug}:`, error);
            throw error;
        }
    },

    /**
     * Create new blog post
     * @param postData - Blog post data
     * @returns Promise<BlogPostResponse>
     */
    create: async (postData: BlogPostRequest): Promise<BlogPostResponse> => {
        try {
            console.log('Creating blog post:', postData);
            const response = await api.post<BlogPostResponse>('/api/admin/blog/posts', postData);
            console.log('Blog post created:', response);
            return response;
        } catch (error) {
            console.error('Error creating blog post:', error);
            throw error;
        }
    },

    /**
     * Update blog post by public_id
     * @param publicId - Blog post public_id (UUID)
     * @param postData - Updated blog post data
     * @returns Promise<BlogPostResponse>
     */
    updateByPublicId: async (publicId: string, postData: BlogPostRequest): Promise<BlogPostResponse> => {
        try {
            if (!publicId || publicId.trim() === '') {
                throw new Error(`Invalid blog post public_id: ${publicId}. Cannot update a post that doesn't exist in the database.`);
            }
            console.log(`Updating blog post ${publicId}:`, postData);
            const response = await api.put<BlogPostResponse>(`/api/admin/blog/posts/public/${publicId}`, postData);
            console.log('Blog post updated:', response);
            return response;
        } catch (error: any) {
            console.error(`Error updating blog post ${publicId}:`, error);
            if (error.response?.data?.error) {
                throw new Error(`Backend error: ${error.response.data.error}`);
            }
            throw error;
        }
    },

    /**
     * Delete blog post by public_id
     * @param publicId - Blog post public_id (UUID)
     * @returns Promise<BlogPostResponse>
     */
    deleteByPublicId: async (publicId: string): Promise<BlogPostResponse> => {
        try {
            const response = await api.delete<BlogPostResponse>(`/api/admin/blog/posts/public/${publicId}`);
            return response;
        } catch (error) {
            console.error(`Error deleting blog post ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Search blog posts
     * @param query - Search query
     * @param status - Filter by status
     * @param page - Page number
     * @param limit - Items per page
     * @returns Promise<BlogPostsListResponse>
     */
    search: async (query: string, status: string = 'all', page: number = 1, limit: number = 10): Promise<BlogPostsListResponse> => {
        try {
            const response = await api.get<BlogPostsListResponse>(
                `/api/admin/blog/posts/search?q=${encodeURIComponent(query)}&status=${status}&page=${page}&limit=${limit}`
            );
            return response;
        } catch (error) {
            console.error('Error searching blog posts:', error);
            throw error;
        }
    },

    /**
     * Increment view count by public_id
     * @param publicId - Blog post public_id (UUID)
     * @returns Promise<BlogPostResponse>
     */
    incrementViewsByPublicId: async (publicId: string): Promise<BlogPostResponse> => {
        try {
            const response = await api.post<BlogPostResponse>(`/api/blog/posts/public/${publicId}/view`);
            return response;
        } catch (error) {
            console.error(`Error incrementing views for post ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get related posts by public_id
     * @param publicId - Blog post public_id (UUID)
     * @param limit - Number of related posts
     * @returns Promise<BlogPostsListResponse>
     */
    getRelatedByPublicId: async (publicId: string, limit: number = 3): Promise<BlogPostsListResponse> => {
        try {
            const response = await api.get<BlogPostsListResponse>(`/api/blog/posts/public/${publicId}/related?limit=${limit}`);
            return response;
        } catch (error) {
            console.error(`Error fetching related posts for ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get all categories
     * @returns Promise<{success: boolean, data: any[]}>
     */
    getAllCategories: async (): Promise<{success: boolean, data: any[]}> => {
        try {
            const response = await api.get<{success: boolean, data: any[]}>('/api/blog/categories');
            return response;
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    },

    /**
     * Get all tags
     * @returns Promise<{success: boolean, data: any[]}>
     */
    getAllTags: async (): Promise<{success: boolean, data: any[]}> => {
        try {
            const response = await api.get<{success: boolean, data: any[]}>('/api/blog/tags');
            return response;
        } catch (error) {
            console.error('Error fetching tags:', error);
            throw error;
        }
    },

    /**
     * Create a new category
     * @param name - Category name
     * @param slug - Category slug
     * @param description - Category description (optional)
     * @returns Promise<{success: boolean, data: any}>
     */
    createCategory: async (name: string, slug: string, description?: string): Promise<{success: boolean, data: any}> => {
        try {
            const payload: any = { name, slug };
            if (description) {
                payload.description = description;
            }
            const response = await api.post<{success: boolean, data: any}>('/api/admin/blog/categories', payload);
            return response;
        } catch (error) {
            console.error('Error creating category:', error);
            throw error;
        }
    },

    /**
     * Update a category
     * @param id - Category ID
     * @param name - Category name
     * @param slug - Category slug
     * @param description - Category description (optional)
     * @returns Promise<{success: boolean, data: any}>
     */
    updateCategory: async (id: number, name: string, slug: string, description?: string): Promise<{success: boolean, data: any}> => {
        try {
            const payload: any = { name, slug };
            if (description) {
                payload.description = description;
            }
            const response = await api.put<{success: boolean, data: any}>(`/api/admin/blog/categories/${id}`, payload);
            return response;
        } catch (error) {
            console.error(`Error updating category ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a category
     * @param id - Category ID
     * @returns Promise<{success: boolean, message: string}>
     */
    deleteCategory: async (id: number): Promise<{success: boolean, message: string}> => {
        try {
            const response = await api.delete<{success: boolean, message: string}>(`/api/admin/blog/categories/${id}`);
            return response;
        } catch (error) {
            console.error(`Error deleting category ${id}:`, error);
            throw error;
        }
    },

    /**
     * Create a new tag
     * @param name - Tag name
     * @param slug - Tag slug
     * @returns Promise<{success: boolean, data: any}>
     */
    createTag: async (name: string, slug: string): Promise<{success: boolean, data: any}> => {
        try {
            const response = await api.post<{success: boolean, data: any}>('/api/admin/blog/tags', { name, slug });
            return response;
        } catch (error) {
            console.error('Error creating tag:', error);
            throw error;
        }
    },

    /**
     * Update a tag
     * @param id - Tag ID
     * @param name - Tag name
     * @param slug - Tag slug
     * @returns Promise<{success: boolean, data: any}>
     */
    updateTag: async (id: number, name: string, slug: string): Promise<{success: boolean, data: any}> => {
        try {
            const response = await api.put<{success: boolean, data: any}>(`/api/admin/blog/tags/${id}`, { name, slug });
            return response;
        } catch (error) {
            console.error(`Error updating tag ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a tag
     * @param id - Tag ID
     * @returns Promise<{success: boolean, message: string}>
     */
    deleteTag: async (id: number): Promise<{success: boolean, message: string}> => {
        try {
            const response = await api.delete<{success: boolean, message: string}>(`/api/admin/blog/tags/${id}`);
            return response;
        } catch (error) {
            console.error(`Error deleting tag ${id}:`, error);
            throw error;
        }
    },

    /**
     * Convert BlogPost (frontend type) to BlogPostRequest (API type)
     * @param post - Frontend blog post
     * @returns BlogPostRequest
     */
    convertToRequest: (post: Partial<BlogPost>): BlogPostRequest => {
        // Ensure publish_date is in YYYY-MM-DD format
        let publishDate = post.publishDate || new Date().toISOString().split('T')[0];
        if (publishDate.includes('T')) {
            publishDate = publishDate.split('T')[0];
        }

        return {
            title: post.title || '',
            slug: post.slug || '',
            content: post.content || '',
            excerpt: post.excerpt || '',
            featured_image: post.featuredImage || '',
            author: post.author || 'Admin',
            publish_date: publishDate,
            status: post.status || 'draft',
            comments_enabled: post.commentsEnabled ?? true,
            // SEO fields
            meta_title: post.seo?.metaTitle || post.title || '',
            meta_description: post.seo?.metaDescription || post.excerpt || '',
            meta_keywords: post.seo?.metaKeywords?.join(', ') || '',
            og_title: post.seo?.ogTitle || post.title || '',
            og_description: post.seo?.ogDescription || post.excerpt || '',
            og_image: post.seo?.ogImage || post.featuredImage || '',
            canonical_url: post.seo?.canonicalUrl || `/blog/${post.slug}` || '',
        };
    },
};

export default blogService;
export type { BlogPostRequest, BlogPostResponse, BlogPostsListResponse };
